<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-10">
          <v-card-actions>
            <v-row>
              <v-col cols="4">
                <v-text-field
                  v-model="search"
                  placeholder="Key in reference number here"
                  append-icon="search"
                  label="Search by reference number"
                  hide-no-data
                  hide-selected
                  clearable 
                />
              </v-col>

              <v-col cols="3">
                <v-autocomplete 
                  v-model="selectedClaimTypes"
                  :items="claimEntitlements"
                  deletable-chips
                  small-chips
                  multiple
                  item-text="name"
                  item-value="id"
                  chips
                  hide-no-data
                  hide-selected
                  clearable
                  label="Claim Type Filter"
                  placeholder="Choose claim types"
                  @change="loadClaimRequests"
                />
              </v-col>

              <v-col cols="2">
                <v-autocomplete
                  v-model="year"
                  label="Year"
                  :items="claimYears"
                  item-value="id"
                  :error-messages="errors.collect('year')"
                  data-vv-name="year"
                  data-vv-as="year"
                  @change="loadClaimRequests"
                />
              </v-col>

              <v-col cols="3">
                <v-autocomplete
                  v-model="employee"
                  :error-messages="errors.collect('employee')"
                  :items="employees"
                  data-vv-name="employee"
                  data-vv-as="employee"
                  item-text="person.name"
                  item-id="employee_uid"
                  return-object
                  label="Claimants"
                  @change="onChangeSelectedEmp(employee)"
                />
              </v-col>
            </v-row>
          </v-card-actions>

          <v-skeleton-loader
            v-bind="{class:'mx-2'}"
            :loading="tableLoading"
            type="table-thead, table-row-divider@9, table-tfoot"
          >
            <v-data-table
              :headers="headers"
              :items="claimRequests"
              :search="search"
              :single-expand="true"
              show-expand
              item-key="id"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>Claim Requests</v-toolbar-title>
                  <my-claim-request-dialog-create
                    v-if="claimEntitlements.length > 0"
                    :employee="employee"
                    :claim-cycles="claimCycles"
                    :is-claim-on-behalf="isClaimOnBehalf"
                    @created="loadClaimRequests"
                  />
                </v-toolbar>
              </template>

              <template v-slot:item.cycle="{ item }">
                <span>{{ item.claim_cycle.start_date }} - {{ item.claim_cycle.end_date }}</span>
                <span
                  v-if="hasInvalidReceiptDateClaim(item) || hasInvalidDeletedClaim(item) || positionHasNoClaim(item)"
                  class="red--text ml-1 font-calibri font-weight-medium"
                >*Invalid claim existed</span>
              </template>

              <template v-slot:item.reference_number="{ item }">
                <span>{{ item.reference_number ? item.reference_number : '-' }}</span>
              </template>

              <template v-slot:item.total_request_amount="{ item }">
                <span>{{ thousandSeparator((item.total_request_amount / 100).toFixed(2)) }}</span>
              </template>

              <template v-slot:item.latest_status="{ item }">
                <span
                  :class="{ 
                    'warning--text': (
                      item.latest_status.id === 100 || 
                      item.latest_status.id === 201 || 
                      item.latest_status.id === 250 || 
                      item.latest_status.id === 260 || 
                      item.latest_status.id === 270 || 
                      item.latest_status.id === 400
                    ),
                    'red--text': (
                      item.latest_status.id === 500 ||
                      item.latest_status.id === 502 ||
                      item.latest_status.id === 300
                    ),
                    'grey--text': (!item.latest_status.id || item.latest_status.id === 501),
                    'green--text': (
                      item.latest_status.id === 200 ||
                      item.latest_status.id === 600
                    ),
                  }"
                >
                  {{ item.latest_status.name ? item.latest_status.name : 'Open' }}
                </span>
              </template>

              <template v-slot:item.action="{ item }">
                <v-btn
                  v-if="item && (!item.approval_request || (item.latest_status && item.latest_status.name === 'Request More'))"
                  color="hrissecondary"
                  class="white--text"
                  :loading="loadingMap[item.id] || false"
                  :disabled="loadingMap[item.id] || (item && item.claim_request_entries.length === 0) || hasInvalidReceiptDateClaim(item) || hasInvalidDeletedClaim(item)"
                  @click="submitForm(item)"
                >
                  Submit
                </v-btn>
                <my-claim-request-entry-dialog-create
                  v-if="item && claimEntitlements.length > 0 && (!item.approval_request || item.latest_status && item.latest_status.name === 'Request More')"
                  :claim-entitlements="claimEntitlements"
                  :claim-request="item"
                  :transaction-date-validity="claimTransactionDateValidityRules"
                  @created="loadClaimRequests"
                />
                <my-claim-request-dialog-delete
                  v-if="item && !item.approval_request"
                  :claim-request="item"
                  @deleted="loadClaimRequests"
                />
                <employee-claims-approvals-dialog-list-histories
                  v-if="item.approval_request"
                  :claim-request="item"
                  :requester="person"
                />
                <my-claim-request-popover-menu-cancel
                  v-if="item && item.approvals.length === 1 && (item.latest_status && item.latest_status.name === 'Pending')"
                  :claim-request="item"
                  @cancelled="loadClaimRequests"
                />
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td
                  v-if="!item.claim_request_entries || item.claim_request_entries.length === 0"
                  :colspan="headers.length"
                  style="background-color: rgba(0, 0, 0, .05);"
                  class="pb-2"
                >
                  <v-simple-table
                    class="px-4 pb-3"
                  >
                    <thead>
                      <tr>
                        <td class="grey--text">
                          No data
                        </td>
                      </tr>
                    </thead>
                  </v-simple-table>
                </td>
                <td
                  v-else
                  :colspan="headers.length"
                  style="background-color: rgba(0, 0, 0, .05);"
                  class="pb-2"
                >
                  <v-simple-table
                    class="px-4 pb-3"
                  >
                    <thead>
                      <tr>
                        <th>
                          <span>Claim Type</span>
                        </th>
                        <th>
                          <span>Receipt Date</span>
                        </th>
                        <th>
                          <span>Receipt No.</span>
                        </th>
                        <th class="text-right">
                          <span>Amount (RM)</span>
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="entry in item.claim_request_entries"
                        :key="entry.id"
                        :class="{
                          'red lighten-5':
                            positionHasNoClaim(item) || (!item.latest_status.id && 
                              (
                                (entry.receipt_date && 
                                  $moment().diff($moment(entry.receipt_date), 'days') >
                                  (claimTransactionDateValidityRules.length > 0 && claimTransactionDateValidityRules[claimTransactionDateValidityRules.length - 1].end_day)
                                ) || (!eligibleClaimTypeIds.includes(entry.claim_type_id))
                              ))
                        }"
                      >
                        <td>
                          <template>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  :color="(!eligibleClaimTypeIds.includes(entry.claim_type_id) && !item.latest_status.id) || positionHasNoClaim(item) ? 'error' : 'grey'"
                                  class="mr-1"
                                  v-on="on"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span> {{ (!eligibleClaimTypeIds.includes(entry.claim_type_id) && !item.latest_status.id) || positionHasNoClaim(item) ? 'No longer eligible to claim' : 'Created at ' + $moment(entry.created_at).format('YYYY-MM-DD hh:mmA') }} </span>
                            </v-tooltip>
                            <span>{{ entry.claim_type.name }} {{ entry.entitlement_date_to_deduct ? '(' + $moment(entry.entitlement_date_to_deduct).format('MMM YYYY') + ')' : '' }}</span>
                          </template>
                        </td>
                        <td>
                          <span 
                            :class="{
                              'red--text':
                                !item.latest_status.id &&
                                ($moment().diff($moment(entry.receipt_date), 'days') >
                                  claimTransactionDateValidityRules[claimTransactionDateValidityRules.length - 1].end_day) 
                            }"
                          >
                            {{ entry.receipt_date ? entry.receipt_date : '-' }}
                          </span>
                        </td>
                        <td>{{ entry.receipt_number ? entry.receipt_number : '-' }}</td>
                        <td class="text-right">
                          {{ thousandSeparator((entry.total_amount / 100).toFixed(2)) }}
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                color="hrissecondary"
                                v-on="on"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            RM{{ thousandSeparator((entry.amount / 100).toFixed(2)) }} x {{ entry.quantity }} unit
                          </v-tooltip>
                        </td>
                        <td>
                          <employee-claims-approvals-entries-dialog-view 
                            :claim-request-entry="entry" 
                          />
                          <my-claim-request-entry-dialog-edit
                            v-if="item && (!item.approval_request || (item.latest_status && item.latest_status.name === 'Request More')) && !hasInvalidReceiptDateClaim(item) && !hasInvalidDeletedClaim(item) && !!item.employee.current_position_among_many.position.has_claim"
                            :entry="entry"
                            :claim-entitlements="claimEntitlements"
                            :claim-request="item"
                            :transaction-date-validity="claimTransactionDateValidityRules"
                            @updated="loadClaimRequests"
                          />
                          <my-claim-request-entry-dialog-delete
                            v-if="item && (!item.approval_request || (item.latest_status && item.latest_status.name === 'Request More'))"
                            :entry="entry"
                            @deleted="loadClaimRequests"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </v-skeleton-loader>

          <v-dialog
            v-model="showRemarkDialog"
            persistent
            max-width="450px"
          >
            <material-card
              :offset="10"
              :full-width="true"
              color="hrissecondary"
              title="Remark Required"
              text="Remark for your late submission"
              class="mb-0"
            >
              <form>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="remark"
                        label="Please provide a remark"
                        outlined
                        rows="3"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </form>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  :disabled="progressing"
                  text
                  class="px-10"
                  @click="handleCancel"
                >
                  Cancel
                </v-btn>
                <v-btn
                  :loading="progressing"
                  :disabled="!remark || progressing"
                  color="hrissecondary"
                  class="px-10 white--text"        
                  @click="callSubmitApi"
                >
                  Submit
                </v-btn>
              </v-card-actions>
            </material-card>
          </v-dialog>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      MyClaimRequestDialogCreate: () => import('@/components/hris/my-claim/request/DialogCreate'),
      MyClaimRequestEntryDialogCreate: () => import('@/components/hris/my-claim/request/entry/DialogCreate'),
      MyClaimRequestDialogDelete: () => import('@/components/hris/my-claim/request/DialogDelete'),
      MyClaimRequestPopoverMenuCancel: () => import('@/components/hris/my-claim/request/PopoverMenuCancel'),
      MyClaimRequestEntryDialogEdit: () => import('@/components/hris/my-claim/request/entry/DialogEdit'),
      MyClaimRequestEntryDialogDelete: () => import('@/components/hris/my-claim/request/entry/DialogDelete'),
      EmployeeClaimsApprovalsEntriesDialogView: () => import('@/components/employee/claims/approvals/entries/DialogView'),
      EmployeeClaimsApprovalsDialogListHistories: () => import('@/components/employee/claims/approvals/DialogListHistories'),
    },
    data () {
      return {
        search: '',
        tableLoading: false,
        headers: [
          {
            text: 'Claimants',
            align: 'left',
            sortable: false,
            value: 'employee.person.name',
          },
          {
            text: 'Apply on Behalf by',
            align: 'left',
            sortable: false,
            value: 'on_behalf_requester.person.name',
          },
          {
            text: 'Claim Cycle',
            align: 'left',
            sortable: false,
            value: 'cycle',
          },
          {
            text: 'Reference No.',
            align: 'center',
            sortable: false,
            value: 'reference_number',
          },
          {
            text: 'Total (RM)',
            align: 'right',
            sortable: false,
            value: 'total_request_amount',
          },
          {
            text: 'Status',
            align: 'left',
            sortable: false,
            value: 'latest_status',
          },
          {
            text: '',
            align: 'left',
            sortable: false,
            value: 'action',
          },
        ],
        claimCycles: [],
        selectedClaimTypes: [],
        claimEntitlements: [],
        claimRequests: [],
        employee: null,
        employees: [],
        claimTransactionDateValidityRules: [],
        showRemarkDialog: false,
        remark: null,
        loadingMap: {},
        progressing: false,
        invalidReceiptDateEntry: false,
        selectedClaimRequest: null,
        person: null,
        eligibleClaimTypeIds: [],
        isClaimOnBehalf: true,
        year: parseInt(this.$moment().format('YYYY')),
        claimYears: [],
      }
    },
    computed: {
      ...mapState(['currentEmployee']),
    },
    watch: {
      search (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.loadData()
        }, 500) /* 500ms throttle */
      },
    },
    created () {
      this.loadData()
      this.loadClaimDateValidityRule()
      this.loadClaimEntitlementYear()
    },
    methods: {
      loadClaimEntitlementYear () {
        this.$api.claimEntitlements().listYear()
          .then(res => {
            this.claimYears = res.data
          })
      },
      loadClaimDateValidityRule () {
        this.$api.claimTransactionDateValidityRules().list()
          .then(claimTransactionDateValidityRules => {
            this.claimTransactionDateValidityRules = claimTransactionDateValidityRules.data
          })
      },
      loadClaimRequests () {
        this.tableLoading = true
        this.claimRequests = []
        this.claimCycles = []

        this.$api.claimRequests().listClaimCycles(this.employee.employee_uid)
          .then(claimCycles => {
            this.claimCycles = claimCycles.data
          })

        this.$api.onBehalfClaimApplications().list(this.prepareFormData())
          .then(claimRequests => {
            this.claimRequests = claimRequests.data.map(request => {
              const approvals = request.approval_request ? request.approval_request.approvals : []
              const sortedApprovals = _.orderBy(approvals, ['created_at'], ['desc'])
              const latestApproval = sortedApprovals[0]

              const latestStatus = latestApproval ? latestApproval.status : {}

              // 1st Level Approved
              let priority = 3
              // Open
              if (!latestApproval) {
                priority = 1
              }
              
              // Request More
              if (latestStatus && latestStatus.id === 300) {
                priority = 0
              }

              // Pending
              if (latestStatus && latestStatus.id === 100) {
                priority = 2
              }

              // Cancel
              if (latestStatus && latestStatus.id === 501) {
                priority = 7
              }

              // In Review
              if (latestStatus && (latestStatus.id === 250 || latestStatus.id === 260 || latestStatus.id === 270)) {
                priority = 4
              }

              // Completed
              if (latestStatus && latestStatus.id === 600) {
                priority = 5
              }

              // Rejected
              if (latestStatus && latestStatus.id === 500) {
                priority = 6
              }
              return {
                ...request,
                created_at: this.$moment(request.created_at).format('YYYY-MM-DD hh:mm A'),
                latest_status: latestStatus,
                approvals: sortedApprovals,
                _priority: priority,
              }
            })

            this.claimRequests = _.orderBy(this.claimRequests, ['_priority', 'claim_cycle.start_date'], ['asc', 'desc'])
            this.tableLoading = false
          })
      },
      onChangeSelectedEmp (emp) {
        this.tableLoading = true

        this.eligibleClaimTypeIds = []
        this.claimEntitlements = []
        this.claimCycles = []
        this.selectedClaimTypes = []

        this.employee = emp
        this.person = emp.person

        this.listEmpClaimEntitlements(emp.employee_uid)
        this.loadClaimRequests(this.selectedClaimTypes)
      },
      loadData () {
        this.tableLoading = true
        
        this.$api.hrisEmployees().listSuperior(this.currentEmployee.employee_uid)
          .then(emp => {
            if (emp.data.length > 0) {
              this.employees = emp.data
              const superiorLvl = this.currentEmployee.employment_hierarchy_level.split('-').slice(0, -1).join('-')

              // get current emp HRIS emp Hierarchy Level then cut the back part.
              this.employee = this.employees.length > 1 ? this.employees.find(employee => employee.employment_hierarchy_level === superiorLvl) : this.employees[0]
              this.person = this.employee.person

              this.listEmpClaimEntitlements(this.employee.employee_uid)
              this.loadClaimRequests(this.selectedClaimTypes)
            } else {
              this.tableLoading = false
            }
          })
      },
      hasInvalidReceiptDateClaim (claimRequest) {
        if (!claimRequest.latest_status.id) {
          const maxEndDay = this.claimTransactionDateValidityRules[this.claimTransactionDateValidityRules.length - 1].end_day

          return claimRequest.claim_request_entries.some(entry => {
            if (!entry.receipt_date) return false

            const daysDiff = this.$moment().diff(this.$moment(entry.receipt_date), 'days')
            return daysDiff > maxEndDay
          })
        }
        return false
      },
      hasInvalidDeletedClaim (claimRequest) {
        if (!claimRequest.latest_status.id) {
          return claimRequest.claim_request_entries.some(entry => !this.eligibleClaimTypeIds.includes(entry.claim_type_id))
        }
        return false
      },
      positionHasNoClaim (claimRequest) {
        if (!claimRequest.employee.current_position_among_many.position.has_claim && (claimRequest.latest_status.id === 100 || claimRequest.latest_status.id === 300)) {
          return true
        }
        return false
      },
      callSubmitApi () {
        this.progressing = true
        this.$api.claimRequests().update(this.selectedClaimRequest.id, { remark: this.remark })
          .then(res => {
            this.$set(this.loadingMap, this.selectedClaimRequest.id, false)
            this.progressing = false
            this.$snack({ message: res.data.status })

            this.remark = null
            this.selectedClaimRequest = null
            this.showRemarkDialog = false

            this.loadClaimRequests()
          })
          .catch(error => {
            this.$set(this.loadingMap, this.selectedClaimRequest.id, false)
            this.progressing = false
            if (error.response.data.message) {
              this.$snack({ color: 'error', message: error.response.data.message })
            } else {
              this.$snack({ color: 'error', message: 'Error! Please contact IT support' })
            }
          })
      },
      submitForm (claimRequest) {
        this.$set(this.loadingMap, claimRequest.id, true)
        this.selectedClaimRequest = claimRequest
        if (!claimRequest.latest_status.id) {
          for (const entry of claimRequest.claim_request_entries) {
            if (entry.receipt_date) {
              const daysDiff = this.$moment().diff(this.$moment(entry.receipt_date), 'days')

              const matchedRule = this.claimTransactionDateValidityRules.find(rule =>
                daysDiff >= rule.start_day && daysDiff <= rule.end_day
              )

              if (matchedRule && matchedRule.requires_remark) {
                this.showRemarkDialog = true
                return
              }
            }
          }
        }

        this.callSubmitApi()
      },
      handleCancel () {
        if (this.selectedClaimRequest.id) {
          this.$set(this.loadingMap, this.selectedClaimRequest.id, false)
        }

        this.progressing = false
        this.showRemarkDialog = false
        this.remark = null
        this.selectedClaimRequest = null
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      listEmpClaimEntitlements (employeeUid) {
        this.$api.claimRequests().listClaimCycles(employeeUid)
          .then(claimCycles => {
            this.claimCycles = claimCycles.data
          })

        this.$api.claimTypes().listEmployeeClaims(employeeUid)
          .then(claimEntitlements => {
            this.claimEntitlements = claimEntitlements.data
            this.claimEntitlements.sort((a, b) => {
              return a.name > b.name ? 1 : -1
            })

            this.claimEntitlements = this.claimEntitlements.map(ett => {
              if (ett.claim_entitlement_settings && ett.claim_entitlement_settings.claim_entitlements && ett.claim_entitlement_settings.claim_entitlements.length > 0) {
                const claimEnts = ett.claim_entitlement_settings.claim_entitlements

                const updatedSettings = claimEnts.map(ce => {
                  const pendingAmt = (ett.pending_claims || [])
                    .filter(claim => {
                      if (claim.entitlement_date_to_deduct) {
                        return claim.entitlement_date_to_deduct === ce.start_date
                      } else {
                        if (!claim.receipt_date) return false
                        const receipt = new Date(claim.receipt_date)
                        const start = new Date(ce.start_date)
                        const end = new Date(ce.end_date)
                        return receipt >= start && receipt <= end
                      }
                    })
                    .reduce((sum, claim) => sum + claim.total_amount, 0)

                  return {
                    ...ce,
                    pending_amt: pendingAmt,
                  }
                })

                return {
                  ...ett,
                  claim_entitlement_settings: {
                    ...ett.claim_entitlement_settings,
                    claim_entitlements: updatedSettings,
                  },
                }
              } else {
                return { ...ett }
              }
            })

            this.eligibleClaimTypeIds = this.claimEntitlements.map(claim => claim.id)
          })
      },
      prepareFormData () {
        return {
          currentEmployeeUid: this.currentEmployee.employee_uid,
          claimTypes: this.selectedClaimTypes,
          employeeUid: this.employee.employee_uid,
          year: this.year,
        }
      },
    },
  }
</script>
